import { FormArray, FormGroup } from '@angular/forms';

import { ratearProporcional } from '@shared/helpers/rateio-valor.util';
import { UtilService } from '../../../../../shared/services/util.service';

/**
 * Os totais do documento, apurados a partir do FormArray de itens (§7).
 *
 * Uma função só, usada em DOIS lugares: a seção de Totais, que exibe, e o `salvar()` do shell,
 * que escreve os valores no request. Antes eram duas contas — os getters da seção e nada no
 * salvamento — e o resultado era o documento gravar imposto zero enquanto a tela mostrava o
 * apurado. O número exibido e o gravado saem daqui, então não têm como divergir.
 *
 * Dinheiro apurado arredonda em 2 casas, ABNT, no RESULTADO da soma — não em cada linha, que já
 * chega arredondada pela apuração do item. Quantidades e pesos ficam como estão: seguem as casas
 * da empresa, e quem formata é o pipe.
 */
export interface TotaisDoDocumento {
    quantidadeDeItens: number;
    somaDasQuantidades: number;
    pesoBruto: number;
    pesoLiquido: number;
    totalDosItens: number;
    valorIpi: number;
    /** ICMS próprio + FCP — o protótipo e a nota destacam os dois juntos. */
    valorIcms: number;
    /** ICMS ST + FCP ST. */
    valorIcmsSt: number;
    valorPis: number;
    valorCofins: number;
    /** ICMS devido à UF de destino (`difal.valorIcmsDestino`). */
    valorDifal: number;
    /** Parcelas do IBS da UF e do município, e o IBS total (a soma das duas). */
    valorIbsEstadual: number;
    valorIbsMunicipal: number;
    valorIbsTotal: number;
    valorCbs: number;
}

export function somarItens(itens: FormArray | null | undefined, caminho: string): number {
    return (itens?.controls ?? []).reduce(
        (soma, linha) => soma + (Number(linha.get(caminho)?.value ?? 0) || 0),
        0,
    );
}

/** Peso é POR UNIDADE do produto: acompanha a quantidade da linha. */
export function somarPonderado(itens: FormArray | null | undefined, caminho: string): number {
    return (itens?.controls ?? []).reduce((soma, linha) => {
        const unitario = Number(linha.get(caminho)?.value ?? 0) || 0;
        const quantidade = Number(linha.get('dados.quantidade')?.value ?? 0) || 0;

        return soma + unitario * quantidade;
    }, 0);
}

export function calcularTotaisDoDocumento(itens: FormArray | null | undefined): TotaisDoDocumento {
    const dinheiro = (caminho: string): number => UtilService.arredondarAbnt(somarItens(itens, caminho), 2);

    return {
        quantidadeDeItens: itens?.length ?? 0,
        somaDasQuantidades: somarItens(itens, 'dados.quantidade'),
        pesoBruto: somarPonderado(itens, 'dados.pesoBruto'),
        pesoLiquido: somarPonderado(itens, 'dados.pesoLiquido'),
        totalDosItens: dinheiro('dados.valorTotal'),
        valorIpi: dinheiro('ipi.valorIpi'),
        valorIcms: UtilService.arredondarAbnt(somarItens(itens, 'icms.valorIcms') + somarItens(itens, 'icms.valorFcp'), 2),
        valorIcmsSt: UtilService.arredondarAbnt(somarItens(itens, 'icms.valorIcmsSt') + somarItens(itens, 'icms.valorFcpSt'), 2),
        valorPis: dinheiro('pis.valorPis'),
        valorCofins: dinheiro('cofins.valorCofins'),
        valorDifal: dinheiro('difal.valorIcmsDestino'),
        valorIbsEstadual: dinheiro('ibsCbs.valorIbsUf'),
        valorIbsMunicipal: dinheiro('ibsCbs.valorIbsMunicipal'),
        valorIbsTotal: dinheiro('ibsCbs.valorIbs'),
        valorCbs: dinheiro('ibsCbs.valorCbs'),
    };
}

/**
 * §7: `itens + despesas + frete − desconto`, em 2 casas ABNT.
 *
 * ICMS, IPI, PIS e COFINS **não entram**, e isso é regra escrita: são informativos aqui e só têm
 * efeito na emissão fiscal. IBS e CBS entram ou não conforme a fórmula da natureza da operação
 * (§7.1) — como essa fórmula é do motor fiscal e o gatilho de cálculo ainda não existe, ficam de
 * fora, que é o comportamento conservador: somá-los sem saber se a natureza manda somar inflaria
 * o total que o cliente assina.
 *
 * É função, e não getter de seção, porque DOIS lugares mostram este número — o campo "Total" da
 * seção de Totais e a cobertura da seção de Pagamento ("Total do pedido · Lançado · Falta lançar")
 * — e eles precisam concordar até o centavo.
 */
export function calcularTotalDoDocumento(form: FormGroup | null | undefined): number {
    const numero = (campo: string): number => Number(form?.get(campo)?.value ?? 0) || 0;
    const itens = form?.get('itens') as FormArray | null;

    return UtilService.arredondarAbnt(
        calcularTotaisDoDocumento(itens).totalDosItens + numero('valorFrete') + numero('valorDespesas') - numero('valorDesconto'),
        2,
    );
}

/**
 * Fração do desconto GLOBAL sobre os itens: `valorDesconto ÷ Σ valorTotal` (a mesma conta de
 * `FatorDescontoGlobal` na API.Vendas). Zero sem desconto ou sem itens com valor — não há do que
 * descontar, e dividir por zero não é resposta.
 */
export function fatorDescontoGlobal(form: FormGroup | null | undefined): number {
    const itens = form?.get('itens') as FormArray | null;
    const soma = calcularTotaisDoDocumento(itens).totalDosItens;
    const desconto = Number(form?.get('valorDesconto')?.value ?? 0) || 0;

    if (soma <= 0 || desconto <= 0) return 0;

    return Math.min(1, desconto / soma);
}

/**
 * D21 - o desconto GLOBAL rateado item a item, a MESMA conta da folha (`RatearDescontoGlobal`):
 * proporcional ao total de cada item, em 2 casas ABNT, com a sobra (positiva ou negativa) no
 * ÚLTIMO item (`ratearProporcional`), e a soma fecha o desconto do documento por construção. Item
 * com total negativo, ou desconto sem item com valor, não tem rateio (zeros): o salvar recusa o
 * documento. Na tela serve à comissão e ao teto; o que se grava é o que a folha calcula.
 */
export function ratearDescontoGlobal(totais: number[], valorDesconto: number): number[] {
    const bases = totais.map(total => Number(total) || 0);
    const desconto = Number(valorDesconto) || 0;
    const soma = bases.reduce((a, base) => a + base, 0);

    if (bases.some(base => base < 0) || (soma === 0 && UtilService.arredondarAbnt(desconto, 2) !== 0)) {
        return bases.map(() => 0);
    }

    return ratearProporcional(bases, desconto, 2);
}

/**
 * Desconto EFETIVO do item sobre o BRUTO (D21, regra do Pedro de 24/09): `1 − (total − rateado) ÷
 * (qtd × preço de lista)`, em %, dos valores da linha — desconto unitário e parcela do documento
 * juntos, contra o preço de lista. Sem lista, o próprio unitário é o bruto. Espelho de
 * `DescontoEfetivoDoItem` da folha; é este número que o teto do perfil compara.
 */
export function descontoEfetivoDoItem(linha: FormGroup): number {
    const numero = (caminho: string): number => Number(linha.get(caminho)?.value ?? 0) || 0;

    // Componente de kit: o desconto do item é o do KIT (§6.9) — o unitário pode carregar o centavo do
    // fechamento, que não é desconto. Compõe com a parcela do desconto global do próprio componente.
    if (linha.get('kit.numeroKit')?.value != null) {
        const dKit = numero('kit.percentualDescontoKit') / 100;
        const total = numero('dados.valorTotal');
        const g = total > 0 ? numero('dados.valorDescontoRateado') / total : 0;

        return UtilService.arredondarAbnt((1 - (1 - dKit) * (1 - g)) * 100, 2);
    }

    const lista = numero('dados.precoBrutoUnitario');
    const bruto = numero('dados.quantidade') * (lista > 0 ? lista : numero('dados.precoLiquidoUnitario'));

    if (bruto <= 0) return 0;

    const liquido = numero('dados.valorTotal') - numero('dados.valorDescontoRateado');

    return UtilService.arredondarAbnt((1 - liquido / bruto) * 100, 2);
}

/** Quantos itens passam do teto (§6.5) com o desconto global composto. `null` de teto = sem teto. */
export function contarItensAcimaDoTeto(itens: FormArray | null | undefined, teto: number | null): number {
    if (teto == null) return 0;

    return (itens?.controls ?? []).filter((linha) =>
        linha.get('dados.idProduto')?.value && descontoEfetivoDoItem(linha as FormGroup) > teto).length;
}

/** Desconto em R$ a partir do percentual sobre os itens, 2 casas — a conta que o servidor refaz. */
export function valorDoDescontoPercentual(form: FormGroup | null | undefined, percentual: number | null | undefined): number {
    const itens = form?.get('itens') as FormArray | null;
    const soma = calcularTotaisDoDocumento(itens).totalDosItens;
    const pct = Math.min(100, Math.max(0, Number(percentual ?? 0) || 0));

    return UtilService.arredondarAbnt(soma * pct / 100, 2);
}
